import { useMutation } from '@apollo/client/react';
import { type BasicCouponFragment, ValidateCouponDocument } from '@drycut/graphql';
import { Button, Modal, Para, Stack } from '@drycut/ui-components';
import { PlusIcon, TagIcon } from '@heroicons/react/20/solid';
import { useEffect, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useSearchParams } from 'react-router';

import CouponForm from './CouponForm';
import CouponMessage from './CouponMessage';

type Props = {
  disabled?: boolean;
  onValidate: (coupon: BasicCouponFragment) => void;
};

// TODO: this is deprecated and was being used before we had the single page checkout
export function CouponInsert({ onValidate, disabled = false }: Props): React.ReactElement {
  const { setValue } = useFormContext();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [couponInfo, setCouponInfo] = useState<BasicCouponFragment | null>(null);
  const [searchParams] = useSearchParams();
  const phoneNumber = useWatch({ name: 'phone' });

  const code = searchParams.get('coupon');

  const [validateCoupon] = useMutation(ValidateCouponDocument);

  // Validate coupon code in the query string (present when the customer comes from a referral link)
  useEffect(() => {
    if (code) {
      validateCoupon({
        variables: {
          input: {
            phoneNumber,
            code,
          },
        },
      }).then(({ data }) => {
        const validatedCoupon = data?.validateCoupon?.coupon;

        if (validatedCoupon) {
          setCouponInfo(validatedCoupon);
          onValidate(validatedCoupon);
        }
      });
    }
  }, [code, phoneNumber, validateCoupon, onValidate]);

  const closeModal = () => {
    setValue('couponCode', '');
    setModalOpen(false);
  };

  const handleValidated = (coupon: BasicCouponFragment) => {
    setCouponInfo(coupon);
    setModalOpen(false);
    onValidate(coupon);
  };

  return (
    <>
      {!couponInfo && (
        <Button type="button" fill="ghost" onClick={() => setModalOpen(true)} disabled={disabled}>
          <Stack direction="row" gap={2}>
            <Para weight="bold">
              Hier <u>Rabattcode</u> eingeben
            </Para>

            <PlusIcon className="size-5" />
          </Stack>
        </Button>
      )}

      {couponInfo && (
        <Stack gap={1}>
          <Stack direction="row" gap={2}>
            <TagIcon className="w-4 h-4 text-primary-600" />
            <Para className="text-primary-600" weight="medium">
              {couponInfo.code}
            </Para>
          </Stack>
          <CouponMessage couponInfo={couponInfo} />
        </Stack>
      )}

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title="Gift Card Code eingeben"
        overlayClose={false}
        closeButton
      >
        <CouponForm onCancel={closeModal} onValidate={handleValidated} />
      </Modal>
    </>
  );
}
